import { motion } from "framer-motion";
import { Award, ArrowRight, ExternalLink } from "lucide-react";
import { useCertifications } from "@/hooks/usePortfolio";
import { SectionTitle } from "@/components/SectionTitle";
import { Link } from "react-router-dom";
import dayjs from "dayjs";

export const Certifications = () => {
  const { data: certifications, isLoading, error } = useCertifications();

  if (isLoading || error || !certifications) return null;
  
  // Sort by issueDate descending, take top 8
  const sortedCertifications = [...certifications]
    .sort((a, b) => new Date(b.issueDate || 0) - new Date(a.issueDate || 0))
    .slice(0, 8);

  if (sortedCertifications.length === 0) return null;

  return (
    <section id="certifications" className="section-pad relative overflow-hidden" data-testid="certifications-section">
      {/* Background glow effects */}
      <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-[hsl(var(--gold))]/5 rounded-full blur-[120px] -translate-y-1/2 pointer-events-none" />
      <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-[hsl(var(--gold-deep))]/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container-x relative z-10">
        <SectionTitle 
          eyebrow="Credentials" 
          title={<>Professional <span className="italic gold-text">Certifications</span></>} 
          subtitle="A showcase of verified skills and continuous learning." 
        />
        
        <div className="relative mt-16 max-w-5xl mx-auto">
          {/* Center timeline line for desktop, left for mobile */}
          <div className="absolute left-[28px] md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[hsl(var(--gold))]/30 to-transparent md:-translate-x-1/2" />
          
          <div className="space-y-12 md:space-y-24">
            {sortedCertifications.map((cert, index) => {
              const isEven = index % 2 === 0;
              const skillsList = cert.skills ? cert.skills.split(',').map(s => s.trim()).filter(Boolean) : [];
              
              return (
                <motion.div
                  key={cert.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className={`relative flex flex-col md:flex-row gap-8 items-center ${isEven ? 'md:flex-row-reverse' : ''}`}
                >
                  {/* Center Node */}
                  <div className="absolute left-[28px] md:left-1/2 top-8 md:top-1/2 w-3 h-3 rounded-full bg-[hsl(var(--gold))] shadow-[0_0_15px_hsl(var(--gold))] md:-translate-x-1/2 md:-translate-y-1/2 z-10 -ml-[5.5px] md:ml-0" />
                  
                  {/* Empty space for alternating layout */}
                  <div className="hidden md:block md:w-1/2" />
                  
                  {/* Content Panel */}
                  <div className="w-full md:w-1/2 pl-16 md:pl-0">
                    <div className={`w-full ${isEven ? 'md:pr-12' : 'md:pl-12'}`}>
                      <div className="group relative rounded-2xl glass grain overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:shadow-[hsl(var(--gold))]/10 border border-black/5 hover:border-[hsl(var(--gold))]/40 dark:border-white/5 dark:hover:border-[hsl(var(--gold))]/40">
                        {/* Soft background glow on hover */}
                        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--gold))]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                        
                        <div className="p-6 md:p-8 flex flex-col sm:flex-row gap-6 relative z-10">
                          {/* Left side text info */}
                          <div className="flex-1 space-y-4">
                            <div>
                              <h3 className="font-serif text-xl sm:text-2xl leading-tight mb-1">{cert.certificationName}</h3>
                              <p className="text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] text-sm font-medium tracking-wide">
                                {cert.issuingOrganization}
                              </p>
                            </div>
                            
                            <div className="flex items-center gap-4 text-xs text-muted-foreground uppercase tracking-widest">
                              <span>{cert.issueDate ? dayjs(cert.issueDate).format('MMM YYYY') : 'N/A'}</span>
                              <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                              <span>{cert.doesNotExpire ? 'No Expiration' : (cert.expirationDate ? dayjs(cert.expirationDate).format('MMM YYYY') : 'Valid')}</span>
                            </div>

                            {cert.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                                {cert.description}
                              </p>
                            )}

                            {skillsList.length > 0 && (
                              <div className="flex flex-wrap gap-2 pt-2">
                                {skillsList.slice(0, 3).map((skill, idx) => (
                                  <span key={idx} className="px-2.5 py-1 rounded-full bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 text-[11px] font-medium tracking-wider text-foreground/70">
                                    {skill}
                                  </span>
                                ))}
                                {skillsList.length > 3 && (
                                  <span className="px-2.5 py-1 rounded-full text-[11px] font-medium text-muted-foreground">
                                    +{skillsList.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Right side thumbnail */}
                          <div className="w-full sm:w-32 h-32 sm:shrink-0 rounded-xl overflow-hidden bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 flex items-center justify-center relative group/img">
                            {cert.certificateImageUrl ? (
                              <img 
                                src={cert.certificateImageUrl} 
                                alt={cert.certificationName}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-110"
                                loading="lazy"
                              />
                            ) : (
                              <Award className="w-10 h-10 text-muted-foreground/30 transition-transform duration-500 group-hover/img:scale-110 group-hover/img:text-[hsl(var(--gold))]/50" />
                            )}
                            
                            {cert.credentialUrl && (
                              <a 
                                href={cert.credentialUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="absolute inset-0 bg-background/40 backdrop-blur-sm opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                                aria-label="View Credential"
                              >
                                <ExternalLink className="w-6 h-6 text-foreground drop-shadow-md" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          
          {/* CTA at the bottom */}
          {certifications.length > 8 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-24 text-center relative z-10"
            >
              <Link 
                to="/certifications"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full glass border border-[hsl(var(--gold))]/30 hover:border-[hsl(var(--gold))]/70 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] font-medium tracking-wide hover:shadow-[0_0_20px_hsl(var(--gold))]/20 transition-all duration-300 group"
              >
                View All Certifications
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
